/**
 * CritiqueModeHeader - Header section for critique mode
 * Feature: Refactoring CollectionCritiqueModePage
 *
 * Displays session/collection title, subtitle, and action buttons
 * Handles title/description editing for session owners
 * Shows AI session indicators and feedback status
 *
 * Feature 080: Added share button for collection owners
 */

import { Button } from '@/components/atoms/Button';
import { Link } from '@/components/atoms/Link';
import { PageHeader, PageHeaderLabel, PageHeaderCommandWell } from '@/components/PageHeader';
import { ContributorsTooltip } from '@/components/composed/ContributorsTooltip';
import { AIFeedbackStatusBadge } from '@/components/composed/AIFeedbackStatusBadge';
import { AISessionBadge } from '@/components/ui/AISessionBadge';
import { CollectionIcon, ShareIcon } from '@/components/icons';
import { collectionEditUrl } from '@/utils/urls';
import type { Collection } from '@/graphql/types';
import type { CritiqueSessionData } from '@/hooks/useCritiqueModeHandlers';

interface CritiqueModeHeaderProps {
  // Session data
  session: CritiqueSessionData | null | undefined;
  collection: Collection;

  // Permissions
  isSessionOwner: boolean | null | undefined;
  isCollectionOwner: boolean | null | undefined;

  // Loading states
  resumeLoading: boolean;
  deleteLoading: boolean;

  // Event handlers
  onTitleChange: (title: string) => Promise<void>;
  onDescriptionChange: (description: string | null) => Promise<void>;
  onDeleteSession: () => Promise<void>;
  onResumeSession: () => Promise<void>;
  onSettingsClick?: () => void;  // Feature 649: Open settings dialog
  onShareClick?: () => void;     // Feature 080: Open share dialog
}

export function CritiqueModeHeader({
  session,
  collection,
  isSessionOwner,
  isCollectionOwner,
  resumeLoading,
  deleteLoading,
  onTitleChange,
  onDescriptionChange,
  onDeleteSession,
  onResumeSession,
  onSettingsClick,
  onShareClick,
}: CritiqueModeHeaderProps) {
  return (
    <PageHeader>
      <PageHeaderLabel
        title={session ? session.title : collection.name}
        subtitle={session ? session.description : undefined}
        icon={session ? <ContributorsTooltip sessionId={session.globalId} className="flex-shrink-0" /> : undefined}
        badge={session?.isAiSession ? <AISessionBadge isAiSession /> : undefined}
        titleEditable={!!(session && isSessionOwner)}
        subtitleEditable={!!(session && isSessionOwner)}
        onTitleChange={session ? onTitleChange : undefined}
        onSubtitleChange={session ? onDescriptionChange : undefined}
        subtitlePlaceholder="Add a description..."
      />
      <PageHeaderCommandWell>
        {/* Feature 071: AI feedback status indicator */}
        {session?.isAiSession && (
          <AIFeedbackStatusBadge
            status={session.aiFeedbackStatus}
            isPaused={session.isPaused}
            onResume={onResumeSession}
            isResuming={resumeLoading}
            className="mr-4"
          />
        )}

        {/* Feature 080: Share button (collection owner only) */}
        {session && isCollectionOwner && onShareClick && (
          <Button
            onClick={onShareClick}
            variant="secondary"
            size="md"
            title="Share this critique session"
            className="flex items-center gap-2"
          >
            <ShareIcon size={16} />
            Share
          </Button>
        )}

        {/* Session settings button (owner only) - Feature 649 */}
        {session && isSessionOwner && onSettingsClick && (
          <Button
            onClick={onSettingsClick}
            variant="secondary"
            size="md"
            title="Session settings"
            className="flex items-center gap-2"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </Button>
        )}
        {/* Delete session button (owner only) */}
        {/* Feature 065: Migrated to GraphQL */}
        {session && isSessionOwner && (
          <Button
            onClick={onDeleteSession}
            variant="danger"
            size="md"
            loading={deleteLoading}
            title="Delete session"
            className="flex items-center gap-2"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </Button>
        )}
        {session && isCollectionOwner && (
          <Link
            to={collectionEditUrl(collection.globalId!, collection.slug)}
            variant="secondary"
            size="md"
            className="flex items-center gap-2"
          >
            <CollectionIcon size={16} />
            Edit
          </Link>
        )}
      </PageHeaderCommandWell>
    </PageHeader>
  );
}
